import { useState, useEffect, useCallback } from "react"
import SupplierPerformanceTable from "./suppliers/SupplierPerformanceTable"
import SupplierCostAnalysis from "./suppliers/SupplierCostAnalysis"
import AutoSwitchModal from "./suppliers/AutoSwitchModal"
import GlobalLoader from "@/components/Loading/GlobalLoader"
import styles from "../analytics.module.css"
import { api } from "@/utils/apiService"
import { useAuth } from "@/components/context/AuthContext"
import SupplierSummary from "./suppliers/SupplierSummary"
import { toast } from "react-toastify"
import { useTheme } from "@/components/context/ThemeContext"

export default function SupplierAnalytics() {
	const {theme} = useTheme()
	const { token } = useAuth()
	const [data, setData] = useState(null)
	const [costAnalysis, setCostAnalysis] = useState(null)
	const [loading, setLoading] = useState(true)
	const [period, setPeriod] = useState("monthly")
	const [sortBy, setSortBy] = useState("reliability")
	const [order, setOrder] = useState("desc")
	const [showAutoSwitch, setShowAutoSwitch] = useState(false)
	const [autoSwitchData, setAutoSwitchData] = useState({
		productId: "",
		currentSupplier: "",
		reason: "",
	})
	const [autoSwitchResult, setAutoSwitchResult] = useState(null)
	const [switchLoading, setSwitchLoading] = useState(false)

	const handleAutoSwitch = async () => {
		setSwitchLoading(true)
		try {
			const res = await api.post("/admin/analytics/suppliers/auto-switch", autoSwitchData, {
				headers: {
					Authorization: `Bearer ${token}`
				}
			})

			console.log(res.data.data)
			setAutoSwitchResult(res.data.data)
			const mssg = "Auto-Switch Successfull"
		} catch (error) {
			console.error("Auto-switch error:", error?.response)
			const mssg = error?.response?.data?.message || "Auto-Switch Error"
			toast.error(mssg)
		} finally {
			setSwitchLoading(false)
		}
	}

	const fetchSupplierData = useCallback(async (period, sortBy, order) => {
		setLoading(true)
		try {
			const res = await api.get("/admin/analytics/suppliers/performance", {
				headers: {
					Authorization: `Bearer ${token}`
				},
				params: {
					period,
					sortBy,
					order
				}
			})

			// console.log(res.data.data)
			setData(res.data.data)
		} catch (error) {
			console.error("Error Fetching Supplier Analysis ::", error)
		} finally {
			setLoading(false)
		}
	}, [])

	const fetchCostAnalysis = useCallback(async (period) => {
		setLoading(true)
		try {
			const res = await api.get("/admin/analytics/suppliers/cost-analysis", {
				headers: {
					Authorization: `Bearer ${token}`
				},
				params: {
					period,
				}
			})

			// console.log(res.data.data)
			setCostAnalysis(res.data.data)
		} catch (error) {
			console.error("Error Fetching Cost Analysis ::", error)
		} finally {
			setLoading(false)
		}
	}, [])

	useEffect(() => {
		fetchSupplierData(period, sortBy, order)
	}, [period, sortBy, order, fetchSupplierData])

	useEffect(() => {
		fetchCostAnalysis(period)
	}, [period, fetchCostAnalysis])

	return (
		<div className={`${styles.analyticsPage} ${theme==="dark"?"dark":""}`}>
			<div className={styles.header}>
				<h1>Supplier Analytics</h1>
				<div className={styles.headerActions}>
					<select value={period} onChange={(e) => setPeriod(e.target.value)}>
						<option value="daily">Daily</option>
						<option value="weekly">Weekly</option>
						<option value="monthly">Monthly</option>
					</select>
					<select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
						<option value="reliability">Reliability</option>
						<option value="profit">Profit</option>
						<option value="orders">Orders</option>
					</select>
					<select value={order} onChange={(e) => setOrder(e.target.value)}>
						<option value="desc">Descending</option>
						<option value="asc">Ascending</option>
					</select>
					<button onClick={() => setShowAutoSwitch(true)} className={styles.primaryButton}>
						Auto Supplier Switch
					</button>
				</div>
			</div>

			{loading ? (
				<GlobalLoader
					isLoading={loading}
					variant="spinner"
					message="Loading Product Analytics"
					fullScreen={false}
				/>
			) : (
				<>
					<div className={styles.summaryCards}>
						<div className={styles.summaryCard}>
							<span className={styles.label}>Total Suppliers</span>
							<span className={styles.value}>{data?.summary.totalSuppliers}</span>
						</div>
						<div className={styles.summaryCard}>
							<span className={styles.label}>Total Orders</span>
							<span className={styles.value}>{data?.summary.totalOrders}</span>
						</div>
						<div className={styles.summaryCard}>
							<span className={styles.label}>Avg Reliability</span>
							<span className={styles.value}>{data?.summary.avgReliability}%</span>
						</div>
						<div className={styles.summaryCard}>
							<span className={styles.label}>Total Revenue</span>
							<span className={styles.value}>₹{data?.summary.totalRevenue.toLocaleString()}</span>
						</div>
						<div className={styles.summaryCard}>
							<span className={styles.label}>Total Profit</span>
							<span className={styles.value}>₹{data?.summary.totalProfit.toLocaleString()}</span>
						</div>
					</div>

					{data?.insights?.recommendations && data?.insights.recommendations.length > 0 && (
						<div className={styles.recommendationsSection}>
							<h3>Supplier Recommendations</h3>
							{data.insights.recommendations.map((rec, index) => (
								<div key={index} className={`${styles.recommendation} ${styles[rec.level]}`}>
									<div className={styles.recHeader}>
										<strong>{rec.supplier}</strong>
										<span className={styles.levelBadge}>{rec.level}</span>
									</div>
									<div className={styles.recIssues}>
										<p>Issues:</p>
										<ul>
											{rec.issues?.map((issue, i) => (
												<li key={i}>{issue}</li>
											))}
										</ul>
									</div>
									<div className={styles.recActions}>
										<p>Recommended Actions:</p>
										<ul>
											{rec.actions?.map((action, i) => (
												<li key={i}>{action}</li>
											))}
										</ul>
									</div>
								</div>
							))}
						</div>
					)}

					<SupplierPerformanceTable suppliers={data?.suppliers} />
					<SupplierCostAnalysis anomalies={costAnalysis?.detailedAnalysis} />
					<SupplierSummary suppliers={costAnalysis?.supplierSummary} />
				</>
			)}

			{showAutoSwitch && (
				<AutoSwitchModal
					setShowAutoSwitch={setShowAutoSwitch}
					setAutoSwitchData={setAutoSwitchData}
					handleAutoSwitch={handleAutoSwitch}
					switchLoading={switchLoading}
					autoSwitchResult={autoSwitchResult}
					autoSwitchData={autoSwitchData}
				/>
			)}
		</div>
	)
}
